/**
 * RunEventRow — compact row for non-message timeline events: orchestrator
 * status, tool calls, sub-agent dispatch/result, errors, background/awaiting
 * markers. Plan and Question get their own cards.
 */

import {
  Wrench,
  BrainCircuit,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Bot,
  PauseCircle,
} from "lucide-react";
import { cn } from "@flowstate/utils";
import type { TimelineEvent, TimelineRun } from "../../engine/cortexStreamReducer";
import { PlanCard } from "./PlanCard";
import { QuestionCard } from "./QuestionCard";

interface RunEventRowProps {
  run: TimelineRun;
  event: TimelineEvent;
  onAnswer?: (runId: string, opts: { approved?: boolean; value?: string }) => void;
}

const STATUS_LABELS: Record<string, string> = {
  thinking: "Thinking",
  routing: "Choosing specialist",
  acting: "Working with tools",
  preparing: "Preparing response",
  answering: "Writing answer",
  specialist_complete: "Specialist finished",
};

const STEP_LADDER = ["thinking", "routing", "acting", "preparing", "answering"];

export function RunEventRow({ run, event, onAnswer }: RunEventRowProps) {
  const data = event.data;

  switch (event.kind) {
    case "plan":
      return (
        <PlanCard
          plan={{
            steps: Array.isArray(data.steps) ? (data.steps as string[]) : [],
            reasoning: data.reasoning as string | undefined,
            complexity: data.complexity as "simple" | "medium" | "complex" | undefined,
          }}
          currentStep={run.currentStep}
          isRunning={run.lifecycle === "running" || run.lifecycle === "awaiting"}
        />
      );

    case "question":
      return (
        <QuestionCard
          runId={run.id}
          question={{
            question: String(data.question ?? ""),
            options: data.options as string[] | undefined,
            allowFreeText: data.allowFreeText !== false,
          }}
          answered={
            run.lifecycle === "awaiting_input" ? undefined : { approved: true }
          }
          onAnswer={(rid, opts) => onAnswer?.(rid, opts)}
          disabled={run.lifecycle !== "awaiting_input"}
        />
      );

    case "status": {
      if (run.lifecycle !== "running" && run.lifecycle !== "awaiting") return null;
      const status = String(data.status ?? "");
      const label = STATUS_LABELS[status] ?? String(data.message ?? status);
      const idx = STEP_LADDER.indexOf(status);
      return (
        <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
          <BrainCircuit className="h-3.5 w-3.5 text-primary animate-pulse" />
          <span>{label}</span>
          {idx >= 0 && (
            <div className="flex gap-1">
              {STEP_LADDER.map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-1 w-4 rounded-full",
                    i <= idx ? "bg-primary" : "bg-muted-foreground/20",
                  )}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    case "tool": {
      const tool = String(data.tool ?? "");
      const phase = data.phase as string | undefined;
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {phase === "result" ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500/80" />
          ) : (
            <Wrench className="h-3.5 w-3.5 text-primary" />
          )}
          <span className="truncate">
            {phase === "start" ? "Calling " : "Finished "}
            <span className="font-medium text-foreground/80">{tool}</span>
          </span>
        </div>
      );
    }

    case "sub_agent": {
      const agents = (data.agents as Array<Record<string, unknown>>) ?? [];
      const isDispatch = data.phase === "dispatch";
      const names = agents
        .map((a) => String(a.displayName ?? a.specialistType ?? ""))
        .filter(Boolean)
        .join(", ");
      const anyFailed = agents.some((a) => a.status === "failed");
      return (
        <div className="flex items-center gap-2 text-xs">
          {isDispatch ? (
            <Bot className="h-3.5 w-3.5 text-primary" />
          ) : anyFailed ? (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500/80" />
          )}
          <span className="text-muted-foreground truncate">
            {isDispatch ? "Dispatched " : "Returned "}
            <span className="text-foreground/80 font-medium">{names}</span>
          </span>
        </div>
      );
    }

    case "specialist_data":
      // Structured payloads (mecha/health blocks) render via the message itself.
      return null;

    case "run_marker": {
      const phase = data.phase as string | undefined;
      if (phase === "awaiting") {
        return (
          <div className="flex items-center gap-2 text-xs rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <PauseCircle className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-muted-foreground">
              Waiting on specialist — Cortex will resume automatically
            </span>
          </div>
        );
      }
      if (phase === "awaiting_input") return null; // QuestionCard carries it
      if (phase === "background") {
        return (
          <div className="flex items-center gap-2 text-xs rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <Clock className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-muted-foreground">
              Moved to background{data.label ? ` — ${String(data.label)}` : ""}
            </span>
          </div>
        );
      }
      return null;
    }

    case "error":
      return (
        <div className="flex items-center gap-2 text-xs rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="leading-snug">{String(data.message ?? "Unknown error")}</span>
        </div>
      );

    default:
      return null;
  }
}
